import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import BusinessIcon from '@material-ui/icons/Business';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { fetchCnpjInfo } from '../../../utils/brasilApi';
import useApi from '../../../utils/useApi';

const supplierFilter = createFilterOptions();

const useStyles = makeStyles((theme) => ({
  uploadBox: {
    border: '2px dashed #b0c4de',
    borderRadius: 12,
    padding: theme.spacing(5),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#f8fafc',
    transition: 'all 0.25s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#f0f7ff',
      transform: 'scale(1.005)',
    },
  },
  issuerCard: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: '#f8fbff',
    border: '1px solid #d4e5f9',
    marginBottom: theme.spacing(2.5),
  },
  sectionTitle: {
    fontWeight: 600,
    color: '#17497F',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  globalControls: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    marginBottom: theme.spacing(2),
  },
}));

const ImportStepUpload = ({
  file,
  analyzing,
  analyzeError,
  issuer,
  setIssuer,
  onFileSelect,
  defaultSupplier,
  setDefaultSupplier,
  defaultCategory,
  setDefaultCategory,
  defaultMargin,
  setDefaultMargin,
  supplierList = [],
  categoryList = [],
  onReloadSuppliers,
  onProceedToReview,
  productsCount = 0,
}) => {
  const classes = useStyles();
  const [creatingSupplier, setCreatingSupplier] = useState(false);

  const [createSupplierLoad] = useApi({
    method: 'post',
    url: '/suppliers',
  });

  // Tentar enriquecer fornecedor silenciosamente via BrasilAPI se CNPJ estiver disponível e não cadastrado
  const handleAutoCreateSupplier = async () => {
    if (!issuer || !issuer.cnpj) return;
    setCreatingSupplier(true);
    const cleanCnpj = issuer.cnpj.replace(/\D/g, '');

    let enrichedData = {
      name: issuer.name || 'Fornecedor NFe',
      pseudonym: issuer.pseudonym || '',
      cnpj: cleanCnpj,
      contractType: 'FIXO',
      city: issuer.city || '',
      neighborhood: issuer.neighborhood || '',
      street: issuer.street || '',
      houseNumber: issuer.houseNumber || '',
      whatsapp: '',
      email: '',
    };

    try {
      const brasilData = await fetchCnpjInfo(cleanCnpj).catch(() => null);
      if (brasilData) {
        enrichedData = {
          ...enrichedData,
          name: brasilData.razao_social || enrichedData.name,
          pseudonym: brasilData.nome_fantasia || enrichedData.pseudonym,
          city: brasilData.municipio || enrichedData.city,
          neighborhood: brasilData.bairro || enrichedData.neighborhood,
          street: brasilData.logradouro || enrichedData.street,
          houseNumber: brasilData.numero || enrichedData.houseNumber,
        };
      }
    } catch {
      // Falha silenciosa no enriquecimento externo
    }

    createSupplierLoad({
      data: enrichedData,
      onCompleted: (res) => {
        setCreatingSupplier(false);
        const supplierName = res?.data?.name || enrichedData.name || issuer.name || 'Fornecedor NFe';
        const newId = res?.data?.id;

        const matched = {
          id: newId,
          name: supplierName,
          cnpj: enrichedData.cnpj,
        };

        if (onReloadSuppliers) onReloadSuppliers();
        if (newId) setDefaultSupplier(newId);
        setIssuer((prev) => ({
          ...prev,
          matchedSupplier: matched,
        }));
      },
      onError: () => {
        setCreatingSupplier(false);
      },
    });
  };

  return (
    <Box>
      {!file ? (
        <Box className={classes.uploadBox} component="label">
          <input
            type="file"
            accept=".xml"
            hidden
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onFileSelect(e.target.files[0]);
              }
            }}
          />
          {analyzing ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <CircularProgress size={44} />
              <Typography variant="body1" style={{ marginTop: 12, fontWeight: 500 }}>
                Lendo e analisando NFe...
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Identificando produtos, códigos de barras, unidades e emitente.
              </Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon
                style={{ fontSize: 56, color: analyzeError ? '#f44336' : '#17497F', marginBottom: 8 }}
              />
              <Typography variant="h6" style={{ fontWeight: 600, color: '#17497F' }}>
                {analyzeError
                  ? 'Erro ao ler arquivo XML. Clique para tentar novamente.'
                  : 'Clique para selecionar ou arraste o arquivo XML da NFe'}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                Suporta notas de entrada modelo 55 de qualquer fornecedor nacional.
              </Typography>
              {analyzeError && (
                <Box mt={2} p={1.5} bgcolor="#ffebee" borderRadius={6}>
                  <Typography color="error" variant="caption">
                    {analyzeError.message || 'Falha na leitura do XML. Verifique se o arquivo é válido.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      ) : (
        <Box>
          {/* Card do Emitente / Fornecedor da Nota */}
          {issuer && (
            <Paper className={classes.issuerCard} elevation={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Emitente da NFe: {issuer.name || 'Não identificado'}
                  </Typography>
                  {issuer.cnpj && (
                    <Chip
                      size="small"
                      label={`CNPJ: ${issuer.cnpj}`}
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </Box>
                {issuer.matchedSupplier ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`Fornecedor Vinculado: ${issuer.matchedSupplier.name || issuer.matchedSupplier.pseudonym || issuer.name || 'Identificado'}`}
                    color="primary"
                    size="small"
                  />
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={creatingSupplier ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}
                    onClick={handleAutoCreateSupplier}
                    disabled={creatingSupplier}
                  >
                    {creatingSupplier ? 'Cadastrando...' : 'Cadastrar Fornecedor Rapidamente'}
                  </Button>
                )}
              </Box>
              {(issuer.city || issuer.pseudonym) && (
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: 'block' }}>
                  {issuer.pseudonym ? `Nome Fantasia: ${issuer.pseudonym} • ` : ''}
                  {issuer.city ? `Município: ${issuer.city}/${issuer.state || ''}` : ''}
                </Typography>
              )}
            </Paper>
          )}

          {/* Configurações Globais / Padrões para a importação */}
          <Paper className={classes.globalControls} elevation={0}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Configurações Padrão da Nota
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Typography variant="caption" style={{ fontWeight: 500 }}>
                  Fornecedor Padrão
                </Typography>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={supplierList.find((s) => Number(s.id) === Number(defaultSupplier)) || supplierList[0] || null}
                  options={supplierList}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  getOptionSelected={(option, value) => Number(option.id) === Number(value?.id)}
                  onChange={(e, newValue) => {
                    if (newValue && newValue.id) setDefaultSupplier(Number(newValue.id));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Selecione o fornecedor" />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography variant="caption" style={{ fontWeight: 500 }}>
                  Categoria Padrão para Novos Produtos
                </Typography>
                <Autocomplete
                  disableClearable
                  size="small"
                  value={categoryList.find((c) => c.id === defaultCategory) || null}
                  options={categoryList}
                  getOptionLabel={(option) => option.name}
                  getOptionSelected={(option, value) => option.id === value?.id}
                  onChange={(e, newValue) => {
                    if (newValue && newValue.id) setDefaultCategory(newValue.id);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Selecione a categoria" />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Typography variant="caption" style={{ fontWeight: 500 }}>
                  Margem de Lucro Padrão
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  variant="outlined"
                  value={defaultMargin}
                  onChange={(e) => setDefaultMargin(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    endAdornment: <Typography variant="caption">%</Typography>,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Barra de Ação de Avanço */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <Typography variant="body2" color="textSecondary">
              <strong>{productsCount}</strong> produto(s) lido(s) do arquivo XML.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              onClick={onProceedToReview}
              style={{ fontWeight: 600, padding: '8px 22px' }}
            >
              Revisar Produtos & Formatos
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ImportStepUpload;
